import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
type Status = 'idle' | 'submitting' | 'success' | 'error';
export function JoinQuestionnaire() {
  const {
    t
  } = useLanguage();
  const [instagram, setInstagram] = useState('');
  const [age, setAge] = useState('');
  const [experience, setExperience] = useState('');
  const [followers, setFollowers] = useState('');
  const [ideas, setIdeas] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'submitting') return;
    setStatus('submitting');
    setErrorMsg('');
    const payload = {
      _subject: 'Dance for Fans — Ground Floor application',
      _captcha: 'false',
      _template: 'table',
      'Instagram handle': instagram || '—',
      Age: age || '—',
      'Experience in content creation': experience || '—',
      'Followers across platforms': followers || '—',
      'Best way to reach you and questions': ideas || '—'
    };
    try {
      const res = await fetch('https://formsubmit.co/ajax/jose@teachmejose.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        throw new Error(`Request failed (${res.status})`);
      }
      const data = await res.json().catch(() => ({}));
      if (data?.success === 'false') {
        throw new Error(data?.message || 'Submission failed');
      }
      setStatus('success');
      setInstagram('');
      setAge('');
      setExperience('');
      setFollowers('');
      setIdeas('');
    } catch (err) {
      console.error(err);
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  };
  const inputClass = 'w-full px-4 py-3 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-magenta-500/60 focus:bg-zinc-900 transition-colors disabled:opacity-60 disabled:cursor-not-allowed';
  const isSubmitting = status === 'submitting';
  return <section className="py-24 px-6 bg-zinc-950 border-y border-zinc-900" data-id="element-94">
      <div className="max-w-2xl mx-auto" data-id="element-95">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-10" data-id="element-96">
          <p className="text-sm uppercase tracking-widest text-zinc-500 font-medium mb-4" data-id="element-97">
            {t('join.tag')}
          </p>
          <h2 className="font-serif text-3xl md:text-4xl mb-4 text-zinc-100" data-id="element-98">
            {t('join.title')}
          </h2>
          <p className="text-zinc-400 text-lg" data-id="element-99">{t('join.subtitle')}</p>
        </motion.div>

        {status === 'success' ? <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="bg-emerald-950/40 border border-emerald-700/50 rounded-3xl p-8 md:p-10 text-center" data-id="element-100">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" data-id="element-101" />
            <h3 className="font-serif text-2xl md:text-3xl text-zinc-100 mb-3" data-id="element-102">
              Thank you — application received.
            </h3>
            <p className="text-zinc-400 text-base mb-6" data-id="element-103">
              Your responses have been sent to jose@teachmejose.com. We'll be in
              touch soon.
            </p>
            <button onClick={() => setStatus('idle')} className="text-emerald-400 hover:text-emerald-300 text-sm font-medium underline underline-offset-4" data-id="element-104">
              Submit another response
            </button>
          </motion.div> : <motion.form onSubmit={handleSubmit} initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="bg-zinc-900/50 border border-zinc-800 rounded-3xl p-6 md:p-10 space-y-5" data-id="element-105">
            {/* Honeypot field for spam */}
            <input type="text" name="_honey" style={{
          display: 'none'
        }} tabIndex={-1} autoComplete="off" data-id="element-106" />

            <div data-id="element-107">
              <label htmlFor="instagram" className="block text-sm font-medium text-zinc-300 mb-2" data-id="element-108">
                {t('join.q1')}
              </label>
              <input id="instagram" type="text" value={instagram} onChange={e => setInstagram(e.target.value)} placeholder="@yourhandle" disabled={isSubmitting} className={inputClass} data-id="element-109" />
            </div>

            <div data-id="element-110">
              <label htmlFor="age" className="block text-sm font-medium text-zinc-300 mb-2" data-id="element-111">
                {t('join.q2')}
              </label>
              <input id="age" type="text" inputMode="numeric" value={age} onChange={e => setAge(e.target.value)} placeholder="18+" disabled={isSubmitting} className={inputClass} data-id="element-112" />
            </div>

            <div data-id="element-113">
              <label htmlFor="experience" className="block text-sm font-medium text-zinc-300 mb-2" data-id="element-114">
                {t('join.q3')}
              </label>
              <textarea id="experience" value={experience} onChange={e => setExperience(e.target.value)} placeholder={t('join.q3Placeholder') as string} rows={3} disabled={isSubmitting} className={`${inputClass} resize-y`} data-id="element-115" />
            </div>

            <div data-id="element-116">
              <label htmlFor="followers" className="block text-sm font-medium text-zinc-300 mb-2" data-id="element-117">
                {t('join.q4')}
              </label>
              <input id="followers" type="text" value={followers} onChange={e => setFollowers(e.target.value)} placeholder={t('join.q4Placeholder') as string} disabled={isSubmitting} className={inputClass} data-id="element-118" />
            </div>

            <div data-id="element-119">
              <label htmlFor="ideas" className="block text-sm font-medium text-zinc-300 mb-2" data-id="element-120">
                {t('join.q5')}
              </label>
              <textarea id="ideas" value={ideas} onChange={e => setIdeas(e.target.value)} placeholder={t('join.q5Placeholder') as string} rows={4} disabled={isSubmitting} className={`${inputClass} resize-y`} data-id="element-121" />
            </div>

            {status === 'error' && <div role="alert" className="flex items-start gap-3 p-4 rounded-xl bg-red-950/40 border border-red-800/50 text-red-200 text-sm" data-id="element-122">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-400" data-id="element-123" />
                <div data-id="element-124">
                  <p className="font-medium mb-1" data-id="element-125">
                    Couldn't send your application.
                  </p>
                  <p className="text-red-300/80 text-xs" data-id="element-126">
                    {errorMsg} You can also email us directly at{' '}
                    <a href="mailto:jose@teachmejose.com" className="underline" data-id="element-127">
                      jose@teachmejose.com
                    </a>
                    .
                  </p>
                </div>
              </div>}

            <div className="pt-4 flex justify-center" data-id="element-128">
              <motion.div animate={isSubmitting ? {} : {
            boxShadow: ['0 0 0 0 rgba(16, 185, 129, 0.45)', '0 0 0 10px rgba(16, 185, 129, 0)', '0 0 0 0 rgba(16, 185, 129, 0)'],
            scale: [1, 1.01, 1]
          }} transition={{
            duration: 2.8,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="inline-block rounded-full" data-id="element-129">
                <button type="submit" disabled={isSubmitting} className="inline-flex items-center gap-3 px-8 py-3.5 bg-emerald-600 text-white rounded-full font-medium text-base hover:bg-emerald-500 transition-colors group shadow-lg shadow-emerald-600/25 disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-130">
                  {isSubmitting ? <>
                      <Loader2 className="w-5 h-5 animate-spin" data-id="element-131" />
                      Sending…
                    </> : <>
                      <Send className="w-5 h-5" data-id="element-132" />
                      {t('join.btn')}
                      <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-133" />
                    </>}
                </button>
              </motion.div>
            </div>
          </motion.form>}
      </div>
    </section>;
}